/**
 * Shared stock page chrome, taken from the Food Safety hero and card.
 * New stock pages use this. Existing stock screens stay as they are.
 */

import styled from 'styled-components/macro'
import { BodyText, H1 } from 'src/branding/typography'
import {
  FOOD_SAFETY_SECONDARY_TEXT,
  FOOD_SAFETY_SURFACE,
} from 'src/constants/foodSafety'

/** Soft lime hero band behind the page title. */
export const StockHeroBand = styled.section`
  margin: 0 0 ${({ theme }) => theme.spacing.md};
  padding: ${({ theme }) => theme.spacing.md};
  border: 1px solid ${FOOD_SAFETY_SURFACE.HERO_BORDER};
  border-radius: ${FOOD_SAFETY_SURFACE.CARD_RADIUS};
  background: ${FOOD_SAFETY_SURFACE.HERO_BG};
  box-shadow: ${FOOD_SAFETY_SURFACE.CARD_SHADOW};
  border-top: 3px solid
    ${({ theme }) => theme.colours.lettuceGreen || '#b5d922'};
  min-width: 0;
`

/** Title and actions inside the hero band. */
export const StockHeaderRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.sm};
  min-width: 0;

  ${({ theme }) => theme.device('md')`
    flex-direction: row;
    justify-content: space-between;
    align-items: flex-start;
  `};
`

export const StockPageTitle = styled(H1)`
  margin: 0;
  font-size: 1.75rem;
  line-height: 1.15;
  letter-spacing: -0.02em;
  color: ${({ theme }) => theme.colours.bbqBlack || '#000'};

  ${({ theme }) => theme.device('md')`
    font-size: 2rem;
  `};
`

export const StockHeroIntro = styled(BodyText)`
  display: block;
  margin: 0.4rem 0 0;
  max-width: 34rem;
  color: ${FOOD_SAFETY_SECONDARY_TEXT};
  font-size: 0.95rem;
  line-height: 1.5;
`

/** Content cards on the stock pages. */
export const StockSurfaceCard = styled.div`
  background: ${({ theme }) => theme.colours.mayoWhite || '#fff'};
  border: 1px solid ${FOOD_SAFETY_SURFACE.HERO_BORDER};
  border-radius: ${FOOD_SAFETY_SURFACE.CARD_RADIUS};
  box-shadow: ${FOOD_SAFETY_SURFACE.CARD_SHADOW};
  padding: ${({ theme }) => theme.spacing.sm};
  min-width: 0;

  ${({ theme }) => theme.device('md')`
    padding: ${theme.spacing.md};
  `};

  &[data-accent='lime'] {
    border-color: ${FOOD_SAFETY_SURFACE.LIME_EDGE};
    border-left: 3px solid
      ${({ theme }) => theme.colours.lettuceGreen || '#b5d922'};
  }

  &[data-next='true'] {
    border-color: ${({ theme }) => theme.colours.lettuceGreen || '#7cb305'};
    box-shadow: ${FOOD_SAFETY_SURFACE.CARD_SHADOW_HOVER};
  }
`

/** The one filled lime action on a stock page. */
export const StockPrimaryButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  margin: 0;
  padding: 0.5rem 1rem;
  border: 1.5px solid ${({ theme }) => theme.colours.lettuceGreen || '#b5d922'};
  border-radius: ${FOOD_SAFETY_SURFACE.CONTROL_RADIUS};
  background: ${({ theme }) => theme.colours.lettuceGreen || '#b5d922'};
  color: ${({ theme }) => theme.colours.seaweedGreen || '#132d07'};
  font-weight: 600;
  font-size: 0.9rem;
  line-height: 1.2;
  white-space: nowrap;
  text-decoration: none;
  cursor: pointer;

  &:hover:not(:disabled),
  &:focus:not(:disabled) {
    background: ${({ theme }) => theme.colours.lettuceGreen || '#b5d922'};
    color: ${({ theme }) => theme.colours.seaweedGreen || '#132d07'};
  }

  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }
`

/** Outline action. It does not compete with the lime primary. */
export const StockQuietButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
  flex: 0 0 auto;
  margin: 0;
  padding: 0.45rem 0.85rem;
  border: 1.5px solid ${({ theme }) => theme.colours.panSilver || '#e0dfdf'};
  border-radius: ${FOOD_SAFETY_SURFACE.CONTROL_RADIUS};
  background: ${({ theme }) => theme.colours.mayoWhite || '#fff'};
  color: ${({ theme }) => theme.colours.seaweedGreen || '#132d07'};
  font-weight: 600;
  font-size: 0.9rem;
  line-height: 1.2;
  white-space: nowrap;
  text-decoration: none;
  cursor: pointer;

  &:hover:not(:disabled),
  &:focus:not(:disabled) {
    background: ${FOOD_SAFETY_SURFACE.LIME_WASH};
    color: ${({ theme }) => theme.colours.seaweedGreen || '#132d07'};
  }

  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }
`
